import React, { useState } from "react";
import Container from "@mui/material/Container";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link } from "react-router-dom";

import {
  Grid,
  IconButton,
  Typography,
  Paper,
  Box,
  SvgIcon,
} from "@mui/material";

import InputLabel from "@mui/material/InputLabel";

const SignUpForm = () => {
  const getApiBaseUrl = () => {
    // Always use production backend (onrender)
    return import.meta.env.VITE_ENVPROD || 'https://activitynavigator-backend.onrender.com';
  };
  const apiBaseUrl = getApiBaseUrl();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    password: "",
    country: "Bulgaria",
    city: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!formData.email_address.trim()) {
      newErrors.email_address = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      newErrors.email_address = "Invalid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const dataToSend = { ...formData, role: "user" };

    try {
      const response = await fetch(`${apiBaseUrl}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        // User created successfully - could navigate to login
      } else {
        const errorData = await response.json();
        setSubmitError(errorData.message || "Error creating user. Please try again.");
      }
    } catch (error) {
      setSubmitError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <Grid
      container
      direction="row"
      xs={12}
      md={12}
      lg={12}
      sx={{ justifyContent: "center", alignItems: "center" }}
    >
      <Grid item xs={3} md={3} lg={3}></Grid>
      <Grid
        item
        xs={6}
        md={6}
        lg={6}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Register
          </Typography>
          
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Personal Information Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                margin="normal"
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                error={!!errors.first_name}
                helperText={errors.first_name}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  },
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                error={!!errors.last_name}
                helperText={errors.last_name}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  },
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Email Address"
                type="email"
                name="email_address"
                value={formData.email_address}
                onChange={handleChange}
                error={!!errors.email_address}
                helperText={errors.email_address}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  },
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  },
                }}
              />
            </Box>

            {/* Location Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Location
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControl 
                fullWidth 
                variant="outlined" 
                margin="normal"
                error={!!errors.country}
              >
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  label="Country"
                  required
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      '&:hover': { borderColor: 'primary.main' },
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <MenuItem value="Bulgaria">Bulgaria</MenuItem>
                  <MenuItem value="USA">USA</MenuItem>
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
              <TextField
                fullWidth
                margin="normal"
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                  },
                }}
              />
            </Box>

            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
              sx={{ mt: 2, minHeight: '44px', transition: 'all 0.2s ease-in-out' }}
            >
              Sign Up
            </LoadingButton>
          </form>
        </Paper>
        <Typography
          variant="outlined"
          component={Link}
          to="/login"
          color="primary"
          sx={{ mt: 1.25 }}
        >
          Already registered? Click here to log in
        </Typography>
      </Grid>
      <Grid item xs={3} md={3} lg={3}></Grid>
    </Grid>
  );
};

export default SignUpForm;
